import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { documents, type TimelineDocument } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useToast } from '@/shared/ui/useToast';

import { enviarArquivo } from '../../documents/envio';

const situacao: Record<
  TimelineDocument['status'],
  { label: string; tone: 'success' | 'warning' | 'neutral' }
> = {
  pendente: { label: 'Pendente', tone: 'warning' },
  recebido: { label: 'Recebido', tone: 'success' },
  dispensado: { label: 'Dispensado', tone: 'neutral' },
};

/**
 * Os documentos que a seguradora pediu a corretora na cotacao dela, com o envio do
 * arquivo (defeito M5 da revisao do main: a corretora via o pedido e nao respondia).
 *
 * O servidor so aceita o envio ao documento pedido a corretora numa operacao dela
 * (`documentos.corretora.create`); o botao aparece com a permissao e com o armazenamento
 * configurado. Sem armazenamento, a instalacao guarda so os dados do documento.
 */
export function BrokerRequestedDocuments({
  operationId,
  documentos,
}: {
  operationId: string;
  documentos: TimelineDocument[];
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeEnviar = can('documentos.corretora.create');
  const temPendente = documentos.some((documento) => documento.status === 'pendente');

  const armazenamento = useQuery({
    queryKey: ['document-storage'],
    queryFn: ({ signal }) => documents.storage(signal),
    staleTime: 5 * 60 * 1000,
    enabled: podeEnviar && temPendente,
  });

  const enviar = useMutation({
    mutationFn: ({ documentId, arquivo }: { documentId: string; arquivo: File }) => {
      if (!armazenamento.data) {
        return Promise.reject(new Error('O armazenamento de arquivos nao respondeu.'));
      }
      return enviarArquivo(documentId, arquivo, armazenamento.data);
    },
    onSuccess: (resultado) => {
      toast.success('Arquivo enviado a seguradora.');
      if (resultado.avisoDeCota) toast.info(resultado.avisoDeCota);
      void queryClient.invalidateQueries({ queryKey: ['quote-timeline', operationId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (documentos.length === 0) {
    return <EmptyState icon="file" title="Nenhum documento solicitado" />;
  }

  const disponivel = armazenamento.data?.available === true;

  return (
    <div className="flex flex-col">
      {podeEnviar && temPendente && armazenamento.data && !disponivel ? (
        <div className="px-5 pt-3">
          <Alert tone="info" title="Envio de arquivo indisponivel">
            O armazenamento de arquivos nao esta configurado nesta instalacao.
          </Alert>
        </div>
      ) : null}
      <ul className="divide-y divide-line">
        {documentos.map((documento) => (
          <DocumentoPedido
            key={documento.id}
            documento={documento}
            podeEnviar={podeEnviar && disponivel && documento.status === 'pendente'}
            aceitos={Object.keys(armazenamento.data?.accepted_types ?? {}).join(',')}
            enviando={enviar.isPending && enviar.variables?.documentId === documento.id}
            onEnviar={(arquivo) => enviar.mutate({ documentId: documento.id, arquivo })}
          />
        ))}
      </ul>
    </div>
  );
}

function DocumentoPedido({
  documento,
  podeEnviar,
  aceitos,
  enviando,
  onEnviar,
}: {
  documento: TimelineDocument;
  podeEnviar: boolean;
  aceitos: string;
  enviando: boolean;
  onEnviar: (arquivo: File) => void;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const estado = situacao[documento.status];

  return (
    <li className="flex flex-wrap items-start justify-between gap-2 px-5 py-3">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink-800">
          {documento.title}
          <Badge tone={estado.tone}>{estado.label}</Badge>
        </p>
        <p className="text-2xs text-ink-500">
          Pedido em {formatDate(documento.requested_at)}
        </p>
        {documento.request_note ? (
          <p className="mt-0.5 text-2xs text-ink-600">{documento.request_note}</p>
        ) : null}
      </div>
      {podeEnviar ? (
        <>
          <input
            ref={entrada}
            type="file"
            className="hidden"
            aria-label={`Arquivo de ${documento.title}`}
            accept={aceitos}
            onChange={(event) => {
              const arquivo = event.target.files?.[0];
              if (arquivo) onEnviar(arquivo);
              event.target.value = '';
            }}
          />
          <Button
            size="sm"
            variant="secondary"
            loading={enviando}
            onClick={() => entrada.current?.click()}
          >
            Enviar arquivo
          </Button>
        </>
      ) : null}
    </li>
  );
}
